import { defineStore } from "pinia";
import { computed, ref } from "vue";
import { useSpeechmaticsTranscriber } from "@/composables/useSpeechmaticsTranscriber";
import type { Transcriber } from "@/models/Conversation";
import { cloneDeep } from "es-toolkit";
import { useZero } from "@/composables/useZero";
import { mutators } from "@loanlink-nl/zero";

export type RecordingSession = {
	meetingId: string;
	stream: MediaStream;
	state: "idle" | "starting" | "recording" | "stopping";
	startedAt?: number;
};

export const useRecorderStore = defineStore("recorder", () => {
	const transcriber = ref<Transcriber | null>(null);
	const recordingSession = ref<RecordingSession | null>(null);

	// State for microphone selection
	const availableMicrophones = ref<MediaDeviceInfo[]>([]);
	const selectedMicrophoneId = ref<string | undefined>();
	const isRequestingPermission = ref(false);

	const durationInSeconds = ref(0);

	function $reset() {
		void stop().then(() => {
			durationInSeconds.value = 0;
			isRequestingPermission.value = false;
			selectedMicrophoneId.value = undefined;
			availableMicrophones.value = [];
			recordingSession.value = null;
		});
	}

	// Get available microphones
	async function getAvailableMicrophones() {
		try {
			// Request permission first
			isRequestingPermission.value = true;

			// Get device list
			let devices = await navigator.mediaDevices
				.enumerateDevices()
				.then((devices) =>
					devices.filter((device) => device.kind === "audioinput"),
				);
			if (
				devices.length === 0 ||
				(devices.length === 1 && devices[0]?.deviceId === "")
			) {
				const stream = await navigator.mediaDevices.getUserMedia({
					audio: true,
				});
				// Release the stream
				stream.getTracks().forEach((track) => track.stop());

				devices = await navigator.mediaDevices.enumerateDevices();
			}

			availableMicrophones.value = devices.filter(
				(device) => device.kind === "audioinput",
			);

			// Pre-select first microphone if available
			if (
				availableMicrophones.value.length > 0 &&
				!selectedMicrophoneId.value
			) {
				selectedMicrophoneId.value = availableMicrophones.value[0]?.deviceId;
			}

			isRequestingPermission.value = false;
			return true;
		} catch {
			isRequestingPermission.value = false;
			return false;
		}
	}

	// Start recording for a meeting (can be different than currently viewed meeting)
	async function start(meetingId: string) {
		// If we're already recording this meeting, don't start again
		if (
			recordingSession.value?.meetingId === meetingId &&
			(recordingSession.value.state === "recording" ||
				recordingSession.value.state === "starting")
		) {
			return { success: true, meetingId: meetingId };
		}

		// Make sure we have a microphone selected
		if (!selectedMicrophoneId.value) {
			if (availableMicrophones.value.length === 0) {
				await getAvailableMicrophones();
			}

			if (
				!selectedMicrophoneId.value &&
				availableMicrophones.value.length > 0
			) {
				selectedMicrophoneId.value = availableMicrophones.value[0]?.deviceId;
			}

			if (!selectedMicrophoneId.value) {
				return { success: false, error: "No microphone available" };
			}
		}

		// Get audio stream
		const stream = await navigator.mediaDevices.getUserMedia({
			audio: { deviceId: { exact: selectedMicrophoneId.value } },
		});

		// Set up the recording session
		recordingSession.value = {
			meetingId,
			stream,
			state: "starting",
		};

		// Start transcription
		transcriber.value = useSpeechmaticsTranscriber();
		await transcriber.value?.start(stream);

		if (recordingSession.value) {
			recordingSession.value.startedAt = Date.now();
			recordingSession.value.state = "recording";
		}

		return { success: true, meetingId };
	}

	// Stop the current recording
	async function stop() {
		if (!recordingSession.value) {
			return { success: false, error: "No active recording session" };
		}

		try {
			const { stream } = recordingSession.value;

			// Update states
			recordingSession.value.state = "stopping";

			// Stop transcription
			await transcriber.value?.stop();
			save();

			// Stop all tracks in the stream
			stream.getTracks().forEach((track) => track.stop());

			// Clear the current recording session
			recordingSession.value = null;

			return { success: true };
		} catch (error) {
			// Clean up even if there's an error
			if (recordingSession.value) {
				recordingSession.value.stream
					.getTracks()
					.forEach((track) => track.stop());

				// Clear the current recording session
				recordingSession.value = null;
			}

			return { success: false, error: String(error) };
		}
	}

	function save() {
		if (!recordingSession.value) {
			return;
		}

		const z = useZero();
		z.value.mutate(
			mutators.conversationTable.saveTranscription({
				id: recordingSession.value.meetingId,
				detailId: crypto.randomUUID(),
				transcriptionResult: cloneDeep(
					transcriber.value?.rawTranscription,
					// biome-ignore lint/suspicious/noExplicitAny: types don't match exactly
				) as any,
			}),
		);
		z.value.mutate(
			mutators.conversationTable.setStatus({
				id: recordingSession.value.meetingId,
				status: "ready_to_summarize",
			}),
		);
	}

	const recording = computed(() => {
		return (
			recordingSession.value !== null &&
			(recordingSession.value.state === "recording" ||
				recordingSession.value.state === "starting")
		);
	});
	const state = computed(() => recordingSession.value?.state);
	const meetingId = computed(() => recordingSession.value?.meetingId ?? "");
	const sentences = computed(() => transcriber.value?.transcription ?? []);
	const startedAt = computed(() => recordingSession.value?.startedAt);

	return {
		state,
		recording,

		start,
		stop,
		save,

		sentences,
		durationInSeconds,

		meetingId,
		startedAt,

		// Microphone
		availableMicrophones,
		selectedMicrophoneId,
		isRequestingPermission,
		getAvailableMicrophones,

		$reset,
	};
});
